import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';
import RangeCalendar from '../components/calendar/RangeCalendar';

interface Vehicle {
  idx: string;
  carname: string;
  carClassname: string;
  special: string;
  locationName: string;
  fuel: string;
  carnum: string;
  company: string;
  caryear: string;
  carmonth: string;
  daymoney: string;
  weekmoney: string;
  people: string;
  CarSomoName: string;
}

interface CarClass {
  carClassName: string;
}

interface CarName {
  carname: string;
}

export const VehiclesPage: React.FC = () => {
  // 기본값: 오늘 1시간 후부터 24시간 후까지
  // 단, 20시 이후면 내일 08시부터 24시간으로 설정
  const getDefaultStartDateTime = () => {
    const now = new Date();
    const currentHour = now.getHours();

    if (currentHour >= 20) {
      // 20시 이후면 내일 08시
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(8, 0, 0, 0);
      return tomorrow;
    } else {
      // 20시 이전이면 1시간 후
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
      return now;
    }
  };

  const getDefaultEndDateTime = () => {
    const start = getDefaultStartDateTime();
    const end = new Date(start);
    end.setHours(end.getHours() + 24); // 시작 시간 + 24시간
    return end;
  };

  const [startDateTime, setStartDateTime] = useState<Date | null>(getDefaultStartDateTime());
  const [endDateTime, setEndDateTime] = useState<Date | null>(getDefaultEndDateTime());
  const [startTime, setStartTime] = useState(() => {
    const start = getDefaultStartDateTime();
    return `${start.getHours().toString().padStart(2, '0')}:00`;
  });
  const [endTime, setEndTime] = useState(() => {
    const end = getDefaultEndDateTime();
    return `${end.getHours().toString().padStart(2, '0')}:00`;
  });
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [selectedCarName, setSelectedCarName] = useState('');
  const [selectedFuels, setSelectedFuels] = useState<string[]>([]);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Date/Time editing modal
  const [showDateTimeModal, setShowDateTimeModal] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [tempStartTime, setTempStartTime] = useState<string>('09:00');
  const [tempEndTime, setTempEndTime] = useState<string>('18:00');

  // Generate time options in 30-minute intervals (08:00 - 22:00)
  const timeOptions = Array.from({ length: 29 }, (_, i) => {
    const totalMinutes = 8 * 60 + i * 30; // Start from 8:00, increment by 30 minutes
    const hour = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
    const minute = (totalMinutes % 60).toString().padStart(2, '0');
    const time = `${hour}:${minute}`;
    return { value: time, label: `${hour}:${minute}` };
  });

  // Fetch car classes
  const { data: classesData } = useQuery({
    queryKey: ['carClasses'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/vehicles/classes');
      return response.data;
    },
  });

  // Fetch car names
  const { data: carNamesData } = useQuery({
    queryKey: ['carNames'],
    queryFn: async () => {
      const response = await apiClient.get<any>('/vehicles/names');
      return response.data;
    },
  });

  const { data: vehiclesData, isLoading, error, refetch } = useQuery({
    queryKey: ['vehicles', startDateTime, startTime, endDateTime, endTime, selectedClasses, selectedCarName, selectedFuels],
    queryFn: async () => {
      if (startDateTime && endDateTime) {
        const startDateStr = startDateTime.toISOString().split('T')[0];
        const endDateStr = endDateTime.toISOString().split('T')[0];
        const start = `${startDateStr} ${startTime}`;
        const end = `${endDateStr} ${endTime}`;
        const payload: any = {
          start_date: start,
          end_date: end,
        };
        if (selectedClasses.length > 0) {
          payload.car_class = selectedClasses.join(',');
        }
        if (selectedCarName) {
          payload.car_name = selectedCarName;
        }
        if (selectedFuels.length > 0) {
          payload.fuel = selectedFuels.join(',');
        }
        const response = await apiClient.post<any>('/vehicles/search', payload);
        return response.data;
      } else {
        const response = await apiClient.get<any>('/vehicles');
        return response.data;
      }
    },
  });

  const classes: CarClass[] = classesData?.data || [];
  const carNames: CarName[] = carNamesData?.data || [];

  const handleSearch = () => {
    if (startDateTime && endDateTime) {
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);

      const start = new Date(startDateTime);
      start.setHours(startHour, startMinute, 0, 0);

      const end = new Date(endDateTime);
      end.setHours(endHour, endMinute, 0, 0);

      if (start >= end) {
        alert('반납 일시는 대여 시작 일시보다 이후여야 합니다.');
        return;
      }
    }

    refetch();
  };

  const handleReset = () => {
    const resetStart = getDefaultStartDateTime();
    const resetEnd = getDefaultEndDateTime();
    setStartDateTime(resetStart);
    setEndDateTime(resetEnd);
    setStartTime(`${resetStart.getHours().toString().padStart(2, '0')}:00`);
    setEndTime(`${resetEnd.getHours().toString().padStart(2, '0')}:00`);
    setSelectedClasses([]);
    setSelectedCarName('');
    setSelectedFuels([]);
  };

  const handleOpenDateTimeModal = () => {
    setTempStartDate(startDateTime);
    setTempEndDate(endDateTime);
    if (startDateTime) {
      const startHour = startDateTime.getHours().toString().padStart(2, '0');
      const startMin = startDateTime.getMinutes().toString().padStart(2, '0');
      setTempStartTime(`${startHour}:${startMin}`);
    }
    if (endDateTime) {
      const endHour = endDateTime.getHours().toString().padStart(2, '0');
      const endMin = endDateTime.getMinutes().toString().padStart(2, '0');
      setTempEndTime(`${endHour}:${endMin}`);
    }
    setShowDateTimeModal(true);
  };

  const handleSaveDateTimeModal = () => {
    if (tempStartDate && tempEndDate) {
      const [startHour, startMin] = tempStartTime.split(':').map(Number);
      const [endHour, endMin] = tempEndTime.split(':').map(Number);

      const newStart = new Date(tempStartDate);
      newStart.setHours(startHour, startMin, 0, 0);
      const newEnd = new Date(tempEndDate);
      newEnd.setHours(endHour, endMin, 0, 0);

      setStartDateTime(newStart);
      setEndDateTime(newEnd);

      // Update time strings for display
      setStartTime(tempStartTime);
      setEndTime(tempEndTime);
    }

    // Use setTimeout to avoid React DOM removeChild error
    setTimeout(() => {
      setShowDateTimeModal(false);
    }, 0);
  };

  const handleRangeSelect = (start: Date | null, end: Date | null) => {
    setTempStartDate(start);
    setTempEndDate(end);
  };

  const handleReservation = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setReservationModalOpen(true);
  };

  const toggleRow = (idx: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idx)) {
        newSet.delete(idx);
      } else {
        newSet.add(idx);
      }
      return newSet;
    });
  };

  const vehicles: Vehicle[] = vehiclesData?.data || [];
  const totalCount = vehiclesData?.totalcount || 0;

  return (
    <Layout>
      <div className="px-2 py-4">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">차량 조회</h1>

        {/* Date Time Modal */}
        {showDateTimeModal && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-30 z-[60]"
              onClick={(e) => {
                e.stopPropagation();
                setTimeout(() => setShowDateTimeModal(false), 0);
              }}
            />

            {/* Modal positioned at center */}
            <div
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-80 max-w-[90vw] z-[70]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-lg shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-3 py-2 rounded-t-lg flex items-center justify-between">
                  <h3 className="font-bold text-sm">날짜 및 시간 선택</h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTimeout(() => setShowDateTimeModal(false), 0);
                    }}
                    className="p-0.5 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-2">
                  {/* Calendar */}
                  <div className="mb-2">
                    <RangeCalendar
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      onSelectRange={handleRangeSelect}
                    />
                  </div>

                  {/* Time Selection */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {/* Start Time */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">대여 시작</label>
                      <select
                        value={tempStartTime}
                        onChange={(e) => setTempStartTime(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {timeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* End Time */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-0.5">반납 시간</label>
                      <select
                        value={tempEndTime}
                        onChange={(e) => setTempEndTime(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        {timeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTimeout(() => setShowDateTimeModal(false), 0);
                      }}
                      className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded transition-colors text-xs font-medium"
                    >
                      취소
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveDateTimeModal();
                      }}
                      className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors text-xs font-medium"
                    >
                      확인
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Search Form */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
          <div className="space-y-4">
            {/* Date Range Display with Button - 가로 배치 */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                대여 기간
              </label>
              <button
                onClick={handleOpenDateTimeModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-left hover:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors bg-white"
                style={{ minWidth: '400px' }}
              >
                {startDateTime && endDateTime ? (
                  <span className="text-sm text-gray-900">
                    {startDateTime.getMonth() + 1}월 {startDateTime.getDate()}일{' '}
                    {startDateTime.getHours().toString().padStart(2, '0')}:{startDateTime.getMinutes().toString().padStart(2, '0')}
                    {' ~ '}
                    {endDateTime.getMonth() + 1}월 {endDateTime.getDate()}일{' '}
                    {endDateTime.getHours().toString().padStart(2, '0')}:{endDateTime.getMinutes().toString().padStart(2, '0')}
                  </span>
                ) : (
                  <span className="text-sm text-gray-500">날짜 및 시간 선택</span>
                )}
              </button>

              <button
                onClick={handleSearch}
                className="px-6 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                조회
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                초기화
              </button>
            </div>

            {/* Advanced Search Toggle */}
            <div className="flex items-center gap-3 border-t pt-4">
              <button
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-indigo-600"
              >
                <span>상세검색</span>
                <svg
                  className={`w-4 h-4 transition-transform ${showAdvancedSearch ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Advanced Search Filters */}
            {showAdvancedSearch && (
              <div className="space-y-4 border-t pt-4">
                {/* Car Class Buttons */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    차량 등급
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {classes.map((cls) => (
                      <button
                        key={cls.carClassName}
                        onClick={() => {
                          setSelectedClasses(prev =>
                            prev.includes(cls.carClassName)
                              ? prev.filter(c => c !== cls.carClassName)
                              : [...prev, cls.carClassName]
                          );
                        }}
                        className={`px-4 py-2 text-sm rounded-md transition-colors ${
                          selectedClasses.includes(cls.carClassName)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {cls.carClassName}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Car Name Buttons */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    차종
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedCarName('')}
                      className={`px-4 py-2 text-sm rounded-md transition-colors ${
                        selectedCarName === ''
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      전체
                    </button>
                    {carNames.map((car) => (
                      <button
                        key={car.carname}
                        onClick={() => setSelectedCarName(car.carname)}
                        className={`px-4 py-2 text-sm rounded-md transition-colors ${
                          selectedCarName === car.carname
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {car.carname}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fuel Type Buttons */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    연료
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['휘발유', 'LPG', '경유', '하이브리드', '전기'].map((fuel) => (
                      <button
                        key={fuel}
                        onClick={() => {
                          setSelectedFuels(prev =>
                            prev.includes(fuel)
                              ? prev.filter(f => f !== fuel)
                              : [...prev, fuel]
                          );
                        }}
                        className={`px-4 py-2 text-sm rounded-md transition-colors ${
                          selectedFuels.includes(fuel)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {fuel}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              검색 결과
              {totalCount > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({totalCount}대)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">검색 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">차량 조회에 실패했습니다.</p>
              <p className="text-sm text-gray-500 mt-2">
                {(error as any)?.response?.data?.detail || (error as Error).message}
              </p>
              {vehiclesData && (
                <div className="mt-4 p-4 bg-gray-100 rounded text-left max-w-2xl mx-auto">
                  <p className="text-xs font-mono text-gray-700">
                    <strong>API 응답:</strong><br />
                    {JSON.stringify(vehiclesData, null, 2)}
                  </p>
                </div>
              )}
            </div>
          ) : !vehiclesData || vehicles.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-2 text-gray-500">예약 가능한 차량이 없습니다.</p>
              {vehiclesData && vehiclesData.code !== "0" && (
                <div className="mt-4 p-4 bg-yellow-50 rounded max-w-2xl mx-auto">
                  <p className="text-sm text-yellow-800">
                    <strong>외부 API 메시지:</strong> {vehiclesData.msg}
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">
                    (응답 코드: {vehiclesData.code})
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      상세
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      차량 ID
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      차량번호
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      차량명
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      연식
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      등급
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      인승
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      평일요금
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      주말/휴일요금
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">
                      액션
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vehicles.map((vehicle) => (
                    <React.Fragment key={vehicle.idx}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-3 whitespace-nowrap text-sm">
                          <button
                            onClick={() => toggleRow(vehicle.idx)}
                            className="text-indigo-600 hover:text-indigo-900 focus:outline-none"
                          >
                            {expandedRows.has(vehicle.idx) ? '▼' : '▶'}
                          </button>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {vehicle.idx}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                          {vehicle.carnum}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {vehicle.carname}
                          </div>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          {vehicle.caryear && vehicle.carmonth ? `${vehicle.caryear}.${vehicle.carmonth}` : vehicle.caryear || '-'}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap">
                          <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded bg-blue-100 text-blue-700">
                            {vehicle.carClassname}
                          </span>
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          {vehicle.people || '-'}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          {vehicle.daymoney ? parseInt(vehicle.daymoney).toLocaleString('ko-KR') : '-'}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                          {vehicle.weekmoney ? parseInt(vehicle.weekmoney).toLocaleString('ko-KR') : '-'}
                        </td>
                        <td className="px-6 py-3 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleReservation(vehicle)}
                            className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                            title="예약"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-600">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                          </button>
                        </td>
                      </tr>
                      {expandedRows.has(vehicle.idx) && (
                        <tr>
                          <td colSpan={10} className="px-6 py-4 bg-gray-50">
                            <div className="text-sm">
                              <div className="font-semibold text-gray-700 mb-2">원본 JSON 데이터:</div>
                              <pre className="bg-white p-4 rounded border border-gray-200 overflow-x-auto text-xs">
                                {JSON.stringify(vehicle, null, 2)}
                              </pre>
                              {vehicle.CarSomoName && (
                                <div className="mt-4">
                                  <div className="font-semibold text-gray-700 mb-1">차량 옵션:</div>
                                  <div className="text-gray-600 text-sm">
                                    {vehicle.CarSomoName.split(',').map((option, idx) => (
                                      <span key={idx} className="inline-block bg-blue-50 text-blue-700 px-2 py-1 rounded mr-2 mb-2">
                                        {option.trim()}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Reservation Modal */}
        {reservationModalOpen && selectedVehicle && startDateTime && endDateTime && (
          <ReservationModal
            vehicle={selectedVehicle}
            startDate={startDateTime.toISOString().split('T')[0]}
            startTime={startTime}
            endDate={endDateTime.toISOString().split('T')[0]}
            endTime={endTime}
            onClose={() => {
              setReservationModalOpen(false);
              setSelectedVehicle(null);
            }}
            onSuccess={() => {
              setReservationModalOpen(false);
              setSelectedVehicle(null);
              refetch();
            }}
          />
        )}
      </div>
    </Layout>
  );
};

// Reservation Modal Component
interface ReservationModalProps {
  vehicle: Vehicle;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ReservationModal: React.FC<ReservationModalProps> = ({
  vehicle,
  startDate,
  startTime,
  endDate,
  endTime,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [hasOwnInsurance, setHasOwnInsurance] = useState<'Y' | 'N'>('N');
  const [pickupLocation, setPickupLocation] = useState('');
  const [returnLocation, setReturnLocation] = useState('');
  const [totalAmount, setTotalAmount] = useState(
    vehicle.daymoney ? parseInt(vehicle.daymoney).toLocaleString('ko-KR') : '0'
  );
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // 모달이 열릴 때 이름 입력창에 포커스
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const startDateTime = new Date(`${startDate}T${startTime}:00`).toISOString();
      const endDateTime = new Date(`${endDate}T${endTime}:00`).toISOString();

      const response = await apiClient.post('/reservations/admin', {
        vehicle_idx: vehicle.idx,
        vehicle_name: vehicle.carname,
        vehicle_class: vehicle.carClassname,
        vehicle_number: vehicle.carnum,
        user_name: name,
        user_phone: phone,
        user_email: null,
        user_birth_date: birthDate || null,
        has_own_insurance: hasOwnInsurance,
        start_date: startDateTime,
        end_date: endDateTime,
        pickup_location: pickupLocation || null,
        return_location: returnLocation || null,
        total_amount: parseInt(totalAmount.replace(/,/g, '')) || 0,
      });

      if (response.status === 200) {
        alert('예약이 완료되었습니다.');
        onSuccess();
      }
    } catch (err: any) {
      console.error('Reservation error:', err);
      setError(err.response?.data?.detail || '예약 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg">
        <div className="bg-white border-b border-gray-200 px-5 py-4 rounded-t-lg">
          <h3 className="text-lg font-semibold text-gray-900">
            예약 등록
          </h3>
        </div>

        <div className="p-5">
          <div className="bg-gray-50 p-3 rounded mb-4">
            <div className="text-sm font-medium text-gray-900">{vehicle.carname}</div>
            <div className="text-xs text-gray-500 mt-1">
              {startDate} {startTime} ~ {endDate} {endTime}
            </div>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  이름 <span className="text-red-500">*</span>
                </label>
                <input
                  ref={nameInputRef}
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  생년월일 (YYYYMMDD)
                </label>
                <input
                  type="text"
                  value={birthDate}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // 숫자만 추출
                    if (value.length <= 8) {
                      if (value.length === 8) {
                        // YYYYMMDD -> YYYY-MM-DD 포맷팅
                        setBirthDate(`${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`);
                      } else {
                        setBirthDate(value);
                      }
                    }
                  }}
                  placeholder="19900909"
                  maxLength={10}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  전화번호 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // 숫자만 추출
                    if (value.length <= 11) {
                      if (value.length >= 10) {
                        // 010-0000-0000 또는 010-000-0000 형식으로 포맷팅
                        if (value.length === 10) {
                          setPhone(`${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`);
                        } else {
                          setPhone(`${value.slice(0, 3)}-${value.slice(3, 7)}-${value.slice(7)}`);
                        }
                      } else {
                        setPhone(value);
                      }
                    }
                  }}
                  placeholder="01000000000"
                  maxLength={13}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  총 금액 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={totalAmount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // 숫자만 추출
                    if (value) {
                      setTotalAmount(parseInt(value).toLocaleString('ko-KR'));
                    } else {
                      setTotalAmount('');
                    }
                  }}
                  placeholder="50000"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  자차가입여부 <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex gap-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="Y"
                      checked={hasOwnInsurance === 'Y'}
                      onChange={(e) => setHasOwnInsurance(e.target.value as 'Y')}
                      className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">예</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="N"
                      checked={hasOwnInsurance === 'N'}
                      onChange={(e) => setHasOwnInsurance(e.target.value as 'N')}
                      className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">아니오</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  배차장소
                </label>
                <input
                  type="text"
                  value={pickupLocation}
                  onChange={(e) => setPickupLocation(e.target.value)}
                  placeholder="예: 부산역"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  회차장소
                </label>
                <input
                  type="text"
                  value={returnLocation}
                  onChange={(e) => setReturnLocation(e.target.value)}
                  placeholder="예: 부산역"
                  className="w-full px-3 py-2 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '처리 중...' : '예약 등록'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
